/**
 * PriorityPicker — 149-cw-r143 (2026-08-31).
 *
 * Compact inline picker untuk ubah priority task. Klik pill → popover dgn 4 opsi
 * warna-coded (Rendah, Sedang, Tinggi, Kritis). Optimistic UI.
 *
 * Props:
 *   task: task object
 *   canEdit: boolean
 *   onChanged: () => void (refresh task)
 */
import { useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, History } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api, { formatDetail } from "@/lib/api";
import { PRIORITY_COLOR, PRIORITY_LABEL, fmtDateTime } from "@/lib/constants";

const OPTS = ["Rendah", "Sedang", "Tinggi", "Kritis"];

export default function PriorityPicker({ task, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedNew, setSelectedNew] = useState(null);

  const history = task.priority_history || [];

  const submit = async (val) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}/priority`, { priority: val, note: note.trim() || null });
      toast.success(`Priority: ${task.priority} → ${val}`);
      setOpen(false);
      setNote("");
      setSelectedNew(null);
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal mengubah priority");
    } finally { setBusy(false); }
  };

  if (!canEdit) {
    return (
      <span className={`px-1.5 py-0.5 text-[10px] font-semibold ${PRIORITY_COLOR[task.priority]}`}>
        {PRIORITY_LABEL[task.priority]}
      </span>
    );
  }

  return (
    <div className="inline-flex items-center gap-1">
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setSelectedNew(null); setNote(""); } }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="task-priority-btn"
            title="Klik untuk ubah priority"
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold hover:ring-2 hover:ring-[#0B1D3A]/20 ${PRIORITY_COLOR[task.priority]}`}
          >
            {PRIORITY_LABEL[task.priority]}
            <ChevronDown size={9} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={4} className="w-56 p-2 rounded-none" data-testid="priority-picker-popover">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A] mb-1.5">Ubah Priority</div>
          <div className="space-y-1">
            {OPTS.map((o) => {
              const isCurrent = o === task.priority;
              const isSelected = o === selectedNew;
              return (
                <button
                  key={o}
                  type="button"
                  disabled={isCurrent || busy}
                  onClick={() => setSelectedNew(o)}
                  data-testid={`priority-opt-${o}`}
                  className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs border transition ${
                    isSelected ? "border-[#0B1D3A] bg-slate-50 ring-1 ring-[#0B1D3A]/20"
                    : isCurrent ? "border-slate-300 bg-slate-100 opacity-60 cursor-not-allowed"
                    : "border-slate-200 hover:border-[#0B1D3A] bg-white"
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`inline-block w-2.5 h-2.5 border ${PRIORITY_COLOR[o]}`} />
                    <span className="font-semibold">{PRIORITY_LABEL[o]}</span>
                  </span>
                  {isCurrent && <span className="text-[9px] uppercase tracking-wider text-slate-500">Sekarang</span>}
                  {isSelected && !isCurrent && <Check size={12} className="text-[#0B1D3A]" />}
                </button>
              );
            })}
          </div>
          {selectedNew && (
            <>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Catatan (opsional): kenapa priority diubah?"
                rows={2}
                maxLength={500}
                data-testid="priority-note-input"
                className="mt-2 w-full text-xs border border-slate-300 rounded-none p-1.5 focus:outline-none focus:border-[#0B1D3A] resize-none"
              />
              <button
                type="button"
                onClick={() => submit(selectedNew)}
                disabled={busy}
                data-testid="priority-submit"
                className="mt-1 w-full h-7 bg-[#0B1D3A] hover:bg-[#122a4d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50"
              >
                {busy ? "Menyimpan…" : `Simpan → ${selectedNew}`}
              </button>
            </>
          )}
        </PopoverContent>
      </Popover>
      {history.length > 0 && (
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          data-testid="priority-history-toggle"
          className="text-[9px] text-slate-500 hover:text-[#0B1D3A] inline-flex items-center gap-0.5"
          title="Lihat riwayat priority"
        >
          <History size={9} /> {history.length}×
        </button>
      )}
      {showHistory && history.length > 0 && (
        <div className="absolute z-30 mt-32 bg-white border border-slate-200 shadow-lg w-72 p-2 -ml-2" data-testid="priority-history-panel">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A]">Riwayat Priority</div>
            <button type="button" onClick={() => setShowHistory(false)} className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-[#0B1D3A]">Tutup</button>
          </div>
          <ul className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
            {[...history].reverse().map((h) => (
              <li key={h.id} className="py-1.5 text-[11px]" data-testid={`priority-history-${h.id}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={`px-1 py-0.5 text-[9px] font-semibold ${PRIORITY_COLOR[h.from]}`}>{h.from}</span>
                  <span className="text-slate-400">→</span>
                  <span className={`px-1 py-0.5 text-[9px] font-semibold ${PRIORITY_COLOR[h.to]}`}>{h.to}</span>
                </div>
                {h.note && <div className="text-slate-600 italic">"{h.note}"</div>}
                <div className="text-[9px] text-slate-500 mt-0.5">{h.by_name} · {fmtDateTime(h.at)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
